import { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Eyebrow from "@/components/common/Eyebrow";
import { INSPIRATION } from "@/constants/homeContent";

const TILE_WIDTH = 360;

export default function InspirationSection() {
  const scrollerRef = useRef(null);

  const scrollBy = (dir) => {
    scrollerRef.current?.scrollBy({ left: dir * (TILE_WIDTH + 24), behavior: "smooth" });
  };

  return (
    <Box component="section" id="inspiration" sx={{ maxWidth: 1400, mx: "auto", px: { xs: 3, lg: 6 }, pt: { xs: 8, md: 10 } }} data-testid="inspiration-section">
      <Box sx={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", mb: 3, gap: 2, flexWrap: "wrap" }}>
        <Box>
          <Eyebrow sx={{ mb: 1 }}>Inspiration</Eyebrow>
          <Typography component="h2" className="font-serif-display" sx={{ fontSize: { xs: 26, md: 32 } }}>Stories, guides and travel ideas.</Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box component={Link} to="/p/about" sx={{ display: "flex", alignItems: "center", gap: 0.5, fontSize: 14, fontWeight: 500, color: "text.primary", textDecoration: "none", "&:hover": { color: "primary.main" } }}>
            View all stories <ArrowRight size={14} />
          </Box>
          <Box sx={{ display: { xs: "none", sm: "flex" }, gap: 0.75 }}>
            <IconButton size="small" onClick={() => scrollBy(-1)} data-testid="inspiration-prev" sx={{ border: 1, borderColor: "divider" }}><ChevronLeft size={16} /></IconButton>
            <IconButton size="small" onClick={() => scrollBy(1)} data-testid="inspiration-next" sx={{ border: 1, borderColor: "divider" }}><ChevronRight size={16} /></IconButton>
          </Box>
        </Box>
      </Box>
      <Box
        ref={scrollerRef}
        className="no-scrollbar"
        data-testid="inspiration-carousel"
        sx={{ display: "flex", gap: 3, overflowX: "auto", scrollSnapType: "x mandatory", pb: 1 }}
      >
        {INSPIRATION.map((a) => (
          <Box
            key={a.title}
            data-testid={`inspiration-${a.title.toLowerCase().replace(/\s+/g, "-")}`}
            sx={{ flex: { xs: "0 0 240px", sm: `0 0 ${TILE_WIDTH}px` }, scrollSnapAlign: "start" }}
          >
            <Box sx={{ aspectRatio: "2 / 1", borderRadius: 3.5, overflow: "hidden", mb: 1.5 }}>
              <Box component="img" src={a.image} alt="" sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
            </Box>
            <Typography sx={{ fontWeight: 600, fontSize: 15, mb: 0.5 }}>{a.title}</Typography>
            <Typography variant="caption" color="text.secondary">{a.readTime}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
